export type ParsedTeamsChannel = {
  channelId: string;
  channelName: string | null;
  tenantId: string | null;
};

// Standard channels are `@thread.tacv2`; older/private ones can be `@thread.skype`.
const CHANNEL_ID_PATTERN = /^19:[^\s/?#]+@thread\.[a-z0-9]+$/i;

const safeDecode = (value: string): string | null => {
  try {
    return decodeURIComponent(value);
  } catch {
    return null;
  }
};

const nonEmpty = (value: string | null): string | null =>
  value === null || value.trim() === '' ? null : value.trim();

/**
 * Accepts what Teams' "Copy link" gives you — a channel link
 * (`/l/channel/<id>/<name>?groupId=…&tenantId=…`) or a message link
 * (`/l/message/<id>/<messageId>?…&channelName=…`) — or a bare `19:…@thread.*`
 * channel ID, and pulls out the channel ID.
 */
export const parseTeamsChannelInput = (
  input: string
): ParsedTeamsChannel | null => {
  const trimmed = input.trim();
  if (trimmed === '') {
    return null;
  }

  const bare = safeDecode(trimmed);
  if (bare !== null && CHANNEL_ID_PATTERN.test(bare)) {
    return { channelId: bare, channelName: null, tenantId: null };
  }

  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return null;
  }

  const segments = url.pathname.split('/').filter((s) => s !== '');
  const kindIndex = segments.findIndex(
    (segment, i) =>
      segment === 'l' &&
      (segments[i + 1] === 'channel' || segments[i + 1] === 'message')
  );
  if (kindIndex === -1) {
    return null;
  }

  const kind = segments[kindIndex + 1];
  const rawId = segments[kindIndex + 2];
  const channelId = rawId === undefined ? null : safeDecode(rawId);
  if (channelId === null || !CHANNEL_ID_PATTERN.test(channelId)) {
    return null;
  }

  const rawName = segments[kindIndex + 3];
  const channelName =
    kind === 'channel'
      ? nonEmpty(rawName === undefined ? null : safeDecode(rawName))
      : nonEmpty(url.searchParams.get('channelName'));

  return {
    channelId,
    channelName,
    tenantId: nonEmpty(url.searchParams.get('tenantId')),
  };
};
